import { useState, useEffect, useCallback, useRef } from 'react';
import { getSupabase, checkSupabaseHealth } from '../lib/supabase';
import { safeQuery, logQueryTiming } from '../lib/queryUtils';

export interface ReviewMedia {
  type: 'photo' | 'video';
  url: string;
  duration?: string;
}

export interface Review {
  id: string;
  name: string;
  email: string;
  location: string;
  rating: number;
  text: string;
  avatar: string;
  verified: boolean;
  helpful: number;
  vehicle_purchased: string;
  dealer_response: string | null;
  media: ReviewMedia[];
  status: string;
  created_at: string;
}

export interface AggregateRatingData {
  ratingValue: string;
  reviewCount: number;
  ratingCounts: Record<number, number>;
  mediaCount: number;
}

// ─── Polling Configuration ───────────────────────────────────────────────────
// Reviews are refreshed on a timer instead of a WebSocket subscription.
// This avoids all realtime/WebSocket connection errors entirely.
const POLL_INTERVAL_MS = 60 * 1000; // Re-fetch every 60 seconds
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

// ─── Module-level singleton cache ─────────────────────────────────────────────
// Prevents redundant Supabase fetches when multiple components call useReviews()
interface ReviewsCache {
  reviews: Review[];
  aggregate: AggregateRatingData;
  fetchedAt: number;
  promise: Promise<void> | null;
}

const cache: ReviewsCache = {
  reviews: [],
  aggregate: {
    ratingValue: '0',
    reviewCount: 0,
    ratingCounts: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 },
    mediaCount: 0,
  },
  fetchedAt: 0,
  promise: null,
};

// Listeners so all mounted hook instances re-render when cache updates
const listeners = new Set<() => void>();

function notifyListeners() {
  listeners.forEach((fn) => fn());
}

/**
 * Computes aggregate rating data from an array of reviews.
 */
function computeAggregate(revs: Review[]): AggregateRatingData {
  if (revs.length === 0) {
    return {
      ratingValue: '0',
      reviewCount: 0,
      ratingCounts: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 },
      mediaCount: 0,
    };
  }

  const total = revs.reduce((sum, r) => sum + r.rating, 0);
  const avg = (total / revs.length).toFixed(1);

  const counts: Record<number, number> = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
  revs.forEach((r) => {
    if (counts[r.rating] !== undefined) counts[r.rating]++;
  });

  const mediaCount = revs.filter(
    (r) => Array.isArray(r.media) && r.media.length > 0
  ).length;

  return {
    ratingValue: avg,
    reviewCount: revs.length,
    ratingCounts: counts,
    mediaCount,
  };
}

/**
 * Fetches reviews from Supabase into the singleton cache.
 * Silently swallows all errors in production — the UI degrades gracefully
 * by showing cached data or an empty state rather than surfacing errors.
 * 
 * ⚠️ CRITICAL: Only fetches from Supabase — no mock data fallback.
 * If Supabase is unavailable, the page shows the "Be Our First Reviewer" empty state.
 */
async function fetchIntoCache(): Promise<void> {
  if (cache.promise) return cache.promise;

  cache.promise = (async () => {
    const startTime = Date.now();

    try {
      // ── Health gate ──────────────────────────────────────────────────────
      // If Supabase is known to be unreachable, skip the fetch entirely.
      // This prevents cascading network errors on every poll tick.
      const healthy = await checkSupabaseHealth();
      if (!healthy) {
        // Leave existing cached data in place — don't wipe it
        return;
      }

      const supabase = await getSupabase();
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 12000);

      const result = await safeQuery(
        supabase
          .from('reviews')
          .select('*')
          .eq('status', 'approved') // ✅ Only fetch approved reviews
          .order('created_at', { ascending: false })
          .abortSignal(controller.signal),
        {
          operation: 'fetch reviews',
          fallbackMessage: 'Unable to load reviews.',
          // Suppress console errors in production — failures are silent
          logErrors: import.meta.env.DEV,
        }
      );

      clearTimeout(timeout);
      logQueryTiming('fetch reviews', startTime);

      if (result.error) {
        // Don't throw — just keep existing cached data
        return;
      }

      const mapped: Review[] = (result.data ?? []).map((r: any) => ({
        ...r,
        media: Array.isArray(r.media) ? r.media : [],
      }));

      cache.reviews = mapped;
      cache.aggregate = computeAggregate(mapped);
      cache.fetchedAt = Date.now();
    } catch {
      // Silently swallow all errors (network failures, DNS errors, timeouts).
      // The UI will continue showing whatever was last cached.
    } finally {
      cache.promise = null;
      notifyListeners();
    }
  })();

  return cache.promise;
}

function isCacheStale(): boolean {
  return Date.now() - cache.fetchedAt > CACHE_TTL_MS;
}

/**
 * Custom hook to fetch, aggregate, and submit reviews.
 *
 * Key behaviours:
 * - Uses polling (every 60s) instead of WebSocket realtime subscriptions
 * - Degrades silently when Supabase is unavailable — no console errors in prod
 * - Shares a module-level cache so only one fetch runs across all consumers
 * - ⚠️ NO MOCK DATA FALLBACK — only shows real Supabase reviews
 */
export function useReviews() {
  const [, forceUpdate] = useState(0);
  const [loading, setLoading] = useState(() => cache.fetchedAt === 0);
  const [error, setError] = useState<string | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Register this instance as a listener so it re-renders on cache updates
  useEffect(() => {
    const rerender = () => {
      setLoading(false);
      forceUpdate((n) => n + 1);
    };
    listeners.add(rerender);
    return () => { listeners.delete(rerender); };
  }, []);

  // Initial fetch + polling setup
  useEffect(() => {
    // Kick off an immediate fetch if cache is empty or stale
    if (isCacheStale() || cache.fetchedAt === 0) {
      setLoading(true);
      fetchIntoCache().catch(() => {
        // Silent — errors are already swallowed inside fetchIntoCache
        setLoading(false);
      });
    }

    // ── Polling ──────────────────────────────────────────────────────────────
    // Schedule periodic re-fetches. Each tick checks health before fetching,
    // so if Supabase is down the poll simply no-ops without any console noise.
    pollTimerRef.current = setInterval(() => {
      fetchIntoCache().catch(() => { /* silent */ });
    }, POLL_INTERVAL_MS);

    return () => {
      if (pollTimerRef.current !== null) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, []);

  const refetch = useCallback(async () => {
    cache.fetchedAt = 0;
    setLoading(true);
    try {
      await fetchIntoCache();
      setError(null);
    } catch {
      // Silent in production
      if (import.meta.env.DEV) {
        setError('Failed to reload reviews');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const submitReview = async (data: {
    name: string;
    email: string;
    vehiclePurchased: string;
    rating: number;
    review: string;
  }) => {
    const startTime = Date.now();

    try {
      // Check health before attempting a write
      const healthy = await checkSupabaseHealth();
      if (!healthy) {
        throw new Error('Service temporarily unavailable. Please try again later.');
      }

      const supabase = await getSupabase();
      const result = await safeQuery(
        supabase.from('reviews').insert({
          name: data.name,
          email: data.email,
          rating: data.rating,
          text: data.review,
          vehicle_purchased: data.vehiclePurchased,
          avatar: '',
          role: '',
          verified: false,
          helpful: 0,
          dealer_response: null,
          media: [],
          status: 'pending',
        }),
        {
          operation: 'submit review',
          fallbackMessage: 'Unable to submit review. Please try again.',
          logErrors: import.meta.env.DEV,
        }
      );

      logQueryTiming('submit review', startTime);

      if (result.error) {
        throw new Error(result.error.message);
      }

      await refetch();
      return true;
    } catch (err) {
      throw new Error(err instanceof Error ? err.message : 'Failed to submit review');
    }
  };

  return {
    reviews: cache.reviews,
    loading,
    error,
    aggregate: cache.aggregate,
    submitReview,
    refetch,
  };
}